import { DateTime } from 'luxon';

/**
 * Convert Date object or ISO string to "YYYY-MM-DD" format.
 * @param {Date|string} date - Date object or ISO string
 * @returns {string|null} ISO date string in "YYYY-MM-DD" format
 */
export const toISODate = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // Already ISO date string or full ISO timestamp - extract date part
    return date.slice(0, 10);
  }
  // Date object - extract date components
  return DateTime.fromJSDate(date).toISODate();
};

/**
 * Convert ISO date string to start of day timestamp in specified timezone.
 * @param {string} isoDate - ISO date string "YYYY-MM-DD"
 * @param {string} timezone - Timezone name (e.g., 'UTC', 'local')
 * @returns {string} Full ISO timestamp at start of day
 */
export const dateToStartOfDay = (isoDate, timezone = 'UTC') => {
  return DateTime.fromISO(isoDate, { zone: timezone }).startOf('day').toISO();
};

/**
 * Convert ISO date string to end of day timestamp in specified timezone.
 * @param {string} isoDate - ISO date string "YYYY-MM-DD"
 * @param {string} timezone - Timezone name (e.g., 'UTC', 'local')
 * @returns {string} Full ISO timestamp at end of day
 */
export const dateToEndOfDay = (isoDate, timezone = 'UTC') => {
  return DateTime.fromISO(isoDate, { zone: timezone }).endOf('day').toISO();
};

/**
 * Get today's date as ISO string.
 * @returns {string} Today's date in "YYYY-MM-DD" format
 */
export const todayISODate = () => {
  return DateTime.now().toISODate();
};

/**
 * Get date N days ago as ISO string.
 * @param {number} days - Number of days to subtract
 * @returns {string} Date in "YYYY-MM-DD" format
 */
export const daysAgoISODate = (days) => {
  return DateTime.now().minus({ days }).toISODate();
};

/**
 * Convert ISO date string to Date object for display purposes.
 * The returned Date is at midnight local time for that calendar date.
 * @param {string} isoDate - ISO date string "YYYY-MM-DD"
 * @returns {Date|null} Date object
 */
export const isoDateToDisplayDate = (isoDate) => {
  if (!isoDate) return null;
  // Parse as local date (no timezone shift)
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Convert Date object from date picker to ISO date string.
 * Extracts the local date components to avoid timezone issues.
 * @param {Date} date - Date object from picker
 * @returns {string|null} ISO date string "YYYY-MM-DD"
 */
export const displayDateToISODate = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format an ISO date or timestamp as a short day and month, e.g. "15 Oct".
 * @param {string} iso - ISO date "YYYY-MM-DD" or full ISO timestamp
 * @param {string} timezone - Timezone the date is read in (e.g., 'UTC', 'local')
 * @returns {string} Formatted day and month, or '' when there is nothing to format
 */
export const formatDayMonth = (iso, timezone = 'UTC') => {
  if (!iso) return '';
  return DateTime.fromISO(iso, { zone: timezone }).toFormat('d LLL');
};

/**
 * Format ISO date string for display.
 * @param {string} isoDate - ISO date string "YYYY-MM-DD"
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted date string
 */
export const formatISODate = (isoDate, locale = 'en-US') => {
  if (!isoDate) return '';
  const date = isoDateToDisplayDate(isoDate);
  return date.toLocaleDateString(locale);
};
